import { useTranslate } from '@refinedev/core';
import { Flex, Typography, theme } from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  MinusOutlined,
  StopOutlined,
  QuestionOutlined,
} from '@ant-design/icons';
import type {
  ContentProductBulkActionOut,
  ContentProductBulkActionOutcomeOut,
  ProductBulkActionStatus,
} from '@/api/generated/schemas';
import type { OutcomeTone } from '@/components/common/OutcomeTone';
import { OutcomeIcon } from '@/components/common/OutcomeIcon';
import { OutcomeRow } from '@/components/common/OutcomeRow';
import { OutcomeSummary } from '@/components/common/OutcomeSummary';

const { Text } = Typography;

interface ProductBulkActionResultProps {
  result: ContentProductBulkActionOut;
  /** Product titles by id; falls back to `#id`. */
  titles?: Record<number, string>;
}

const TONES: Record<ProductBulkActionStatus, OutcomeTone> = {
  enqueued: 'success',
  nothing_to_do: 'neutral',
  blocked: 'warning',
  failed: 'error',
  unknown_product: 'neutral',
};

/** Filled means the action ran; outlined means it looked and did nothing. */
const ICONS: Record<
  ProductBulkActionStatus,
  { glyph: React.ReactNode; variant: 'filled' | 'outlined' }
> = {
  enqueued: { glyph: <CheckOutlined />, variant: 'filled' },
  failed: { glyph: <CloseOutlined />, variant: 'filled' },
  blocked: { glyph: <StopOutlined />, variant: 'filled' },
  unknown_product: { glyph: <QuestionOutlined />, variant: 'outlined' },
  nothing_to_do: { glyph: <MinusOutlined />, variant: 'outlined' },
};

/** Summary bar plus one row per product, in the order the backend returned them. */
export const ProductBulkActionResult = ({
  result,
  titles,
}: ProductBulkActionResultProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const k = (key: string, opts?: Record<string, unknown>) =>
    t(`content_catalog.products.bulk.result.${key}`, opts);

  const total = result.outcomes.length;
  const problems = result.blocked + result.failed + result.unknown_product;

  const detail = (o: ContentProductBulkActionOutcomeOut) => {
    const lines: string[] = [];
    if (o.status === 'enqueued' && (o.enqueued ?? 0) > 0)
      lines.push(k('enqueued_jobs', { count: o.enqueued }));
    if (o.blocked_reason) lines.push(o.blocked_reason);
    if (o.error) lines.push(o.error);
    for (const s of o.skipped ?? [])
      lines.push(
        `${s.subject}${s.subject_id != null ? ` #${s.subject_id}` : ''}: ${s.reason}`
      );
    return lines;
  };

  return (
    <Flex vertical gap={token.marginSM}>
      <OutcomeSummary
        headline={
          problems > 0
            ? k('header_problems', { count: problems })
            : k('header_ok')
        }
        aside={k('products', { count: total })}
        percent={total ? Math.round((result.enqueued / total) * 100) : 0}
        exception={result.failed > 0}
        progressLabel={k('products', { count: total })}
      />
      <Flex
        vertical
        gap={token.marginXS}
        style={{ maxHeight: 420, overflowY: 'auto' }}
      >
        {result.outcomes.map((o) => {
          const tone = TONES[o.status];
          const badge = ICONS[o.status];
          const lines = detail(o);
          return (
            <OutcomeRow
              key={o.product_id}
              tone={tone}
              muted={
                o.status === 'nothing_to_do' || o.status === 'unknown_product'
              }
              icon={
                <OutcomeIcon tone={tone} variant={badge.variant}>
                  {badge.glyph}
                </OutcomeIcon>
              }
              title={
                <Flex gap={token.marginXS} align="baseline" wrap>
                  <Text strong>
                    {titles?.[o.product_id] ?? `#${o.product_id}`}
                  </Text>
                  <Text type="secondary">#{o.product_id}</Text>
                </Flex>
              }
            >
              <Text
                type={o.status === 'failed' ? 'danger' : 'secondary'}
                style={{
                  display: 'block',
                  color:
                    o.status === 'enqueued' ? token.colorSuccess : undefined,
                }}
              >
                {k(`status.${o.status}`)}
              </Text>
              {lines.map((line, i) => (
                <Text key={i} type="secondary" style={{ display: 'block' }}>
                  {line}
                </Text>
              ))}
            </OutcomeRow>
          );
        })}
      </Flex>
    </Flex>
  );
};
